import React, { Fragment } from "react";
import {
  addField,
  getDefaultSchemaForFieldType,
  makeUniqueFieldName,
  type PathArray,
  type SubmissionFieldTypeWithSchema,
} from "common/utils/formStructure";
import { fieldCategoryToFieldTypesMap } from "../../constants";
import { EDITOR_TYPE, editorTypeToFieldPermissionsMap } from "../../editorType";
import { useBuilderFormContext, useBuilderWatch } from "../../form/form";
import { VIEWER_TYPE, viewerTypeToFieldPermissionsMap } from "../../viewerType";
import {
  BuilderFieldButton,
  BuilderFieldCategoryName,
  BuilderFieldListItem,
  BuilderFieldsList,
} from "./__styles__/shared";

type FieldsListProps = {
  atFieldPath: PathArray;
};

export function FieldsList({ atFieldPath }: FieldsListProps) {
  const { getValues, setValue } = useBuilderFormContext();
  const getDefaultSchema = useGetDefaultSchemaForFieldType();

  return (
    <div>
      {Object.entries(fieldCategoryToFieldTypesMap).map(
        ([category, fieldTypes]) => (
          <Fragment key={category}>
            <BuilderFieldCategoryName>{category}</BuilderFieldCategoryName>
            <BuilderFieldsList>
              {fieldTypes.map(fieldType => (
                <BuilderFieldListItem key={fieldType.name}>
                  <BuilderFieldButton
                    styleVariant="ghost"
                    leftIconName={fieldType.icon}
                    aria-label={`Add ${fieldType.label} field`}
                    onClick={() => {
                      setValue(
                        "formStructure",
                        addField({
                          formStructure: getValues().formStructure,
                          fieldSchema: getDefaultSchema(fieldType.name),
                          fieldName: makeUniqueFieldName(),
                          targetPath: atFieldPath,
                        }),
                        { shouldDirty: true },
                      );
                    }}
                  >
                    {fieldType.label}
                  </BuilderFieldButton>
                </BuilderFieldListItem>
              ))}
            </BuilderFieldsList>
          </Fragment>
        ),
      )}
    </div>
  );
}

type PermissionsOptions = {
  canCreateRolesAndGroupIds: Array<string>;
  hiddenFromPublicByDefault: boolean;
};

function getDefaultFieldPermissions({
  canCreateRolesAndGroupIds,
  hiddenFromPublicByDefault,
}: PermissionsOptions) {
  return {
    "ui:viewOnCreate": canCreateRolesAndGroupIds,
    "ui:viewOnRetrieve":
      hiddenFromPublicByDefault ?
        viewerTypeToFieldPermissionsMap[VIEWER_TYPE.TEAM]
      : viewerTypeToFieldPermissionsMap[VIEWER_TYPE.TEAM_AND_GUESTS],
    "ui:canUpdate": editorTypeToFieldPermissionsMap[EDITOR_TYPE.TEAM],
  };
}

function getDefaultSchemaForFieldTypeWithPermissions<
  FieldType extends SubmissionFieldTypeWithSchema,
>({
  fieldType,
  canCreateRolesAndGroupIds,
  hiddenFromPublicByDefault,
}: { fieldType: FieldType } & PermissionsOptions) {
  const defaultSchema = getDefaultSchemaForFieldType(fieldType);

  return {
    ...defaultSchema,
    uiSchema: {
      ...defaultSchema.uiSchema,
      ...getDefaultFieldPermissions({
        canCreateRolesAndGroupIds,
        hiddenFromPublicByDefault,
      }),
    },
  };
}

function useGetDefaultSchemaForFieldType() {
  const [canCreateRolesAndGroupIds, hiddenFromPublicByDefault] =
    useBuilderWatch(["canCreateRolesAndGroupIds", "hiddenFromPublicByDefault"]);

  return (fieldType: SubmissionFieldTypeWithSchema) => {
    return getDefaultSchemaForFieldTypeWithPermissions({
      fieldType,
      canCreateRolesAndGroupIds,
      hiddenFromPublicByDefault,
    });
  };
}
